import { yupResolver } from '@hookform/resolvers/yup';
import CheckCircleSharpIcon from '@mui/icons-material/CheckCircleSharp';
import { Box, InputAdornment, Switch, Typography, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { NumberFormatCustom } from '~/components/Chat/AccessoriesValue';
import Input from '~/components/Input';
import InputSelect from '~/components/InputSelect';
import { useContractLoader } from '~/hooks/useContractLoader';
import { ContractPostEquipment, Manufacturer } from '~/services/modules/contract/types';
import { IFile } from '~/services/modules/contract/types';
import useStore, { storeState } from '~/store';
import { getErrorMessage, logErrorWithContext } from '~/utils/errorHandler';
import searchNameEquipment from '~/utils/searchNameEquipment';
import { VoucherError } from '../../step4/VoucherError';
import type { FilesError, TypeFile } from '../../step4/hooks/types';
import schema from '../../step3/validation';
import { CONTRACT_JOURNEY_APP_IDS, contractStepField } from '../contractAppIds';
import { contractPath } from '../buildContractSearchParams';
import ContractJourneyShell from '../components/ContractJourneyShell';
import ContractStepCard from '../components/ContractStepCard';
import ContractUploadSection from '../components/ContractUploadSection';
import { getContractUploadFields } from '../getContractUploadFields';
import { validateContractFiles } from '../validateContractFiles';

type EquipmentForm = {
  model: string;
  manufacturer_id: string;
  serial_number: string;
};

export default function ContractStep2Page() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [hasVoucherError, setHasVoucherError] = useState(false);
  const [fileErrors, setFileErrors] = useState<Partial<Record<TypeFile, boolean>>>({});
  const [files, setFiles] = useState<Partial<Record<TypeFile, IFile | null>>>({});

  const { contract_id } = useContractLoader({ stepName: 'contract_journey_step2' });

  const {
    manufacturers,
    contractAsyncGetManufacturer,
    contractAsyncPostEquipment,
    contractAsyncPostFiles,
    contractAsyncGetVoucher,
    voucher,
    quote,
    insured_item,
    quoteHasInvoice,
    insuredHasInvoice,
    productId
  } = useStore((store) => ({
    contractAsyncGetManufacturer: store.contractAsyncGetManufacturer,
    contractAsyncPostEquipment: store.contractAsyncPutEquipment,
    contractAsyncPostFiles: store.contractAsyncPostFiles,
    contractAsyncGetVoucher: store.contractAsyncGetVoucher,
    voucher: store.voucher,
    manufacturers: store.manufacturers,
    quote: store.quote,
    insured_item: store.insured_item,
    quoteHasInvoice: store.quote.hasInvoice,
    insuredHasInvoice: store.insured_item.has_invoice,
    productId: (store.plan?.products ?? []).map((product) => product.product_id)
  }));

  const hasInvoice = insuredHasInvoice || quoteHasInvoice || false;
  const isNew = insured_item?.is_new ?? quote?.isNewEquipment ?? false;
  const equipmentTypeId = insured_item?.equipment_type_id;

  const uploadFields = useMemo(
    () =>
      getContractUploadFields({
        hasInvoice,
        isNew,
        productId,
        equipmentTypeId
      }),
    [equipmentTypeId, hasInvoice, isNew, productId]
  );

  const [displayValues, setDisplayValues] = useState({
    typeEquipment: '',
    value: 0,
    accessoriesValue: 0,
    yearOfManufacture: '',
    hasInvoiceDisplay: false,
    isNewDisplay: false
  });

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, dirtyFields }
  } = useForm<EquipmentForm>({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    contractAsyncGetManufacturer();
  }, [contractAsyncGetManufacturer]);

  useEffect(() => {
    const orgParam = searchParams.get('org');
    if (!hasInvoice && contract_id) {
      contractAsyncGetVoucher(contract_id as unknown as number, orgParam ?? undefined).catch(() => {
        setHasVoucherError(true);
      });
    }
  }, [contractAsyncGetVoucher, contract_id, hasInvoice, searchParams]);

  useEffect(() => {
    const hasInsuredItem =
      insured_item &&
      (insured_item.equipment_type != null ||
        insured_item.insured_amount != null ||
        insured_item.equipment_type_id != null);

    if (hasInsuredItem) {
      setDisplayValues({
        typeEquipment: insured_item.equipment_type || insured_item.type_insured_item || '',
        value: insured_item.insured_amount ?? 0,
        accessoriesValue: insured_item.total_value_accessories ?? 0,
        yearOfManufacture: String(insured_item.year_of_manufacture ?? ''),
        hasInvoiceDisplay: insured_item.has_invoice ?? false,
        isNewDisplay: insured_item.is_new ?? false
      });
      return;
    }

    if (quote?.equipment != null || quote?.equipmentValue != null) {
      setDisplayValues({
        typeEquipment: searchNameEquipment(quote.equipment),
        value: quote.equipmentValue ?? 0,
        accessoriesValue: quote.accessoriesValue ?? 0,
        yearOfManufacture: String(quote.yearOfManufacture ?? ''),
        hasInvoiceDisplay: quote.hasInvoice ?? false,
        isNewDisplay: quote.isNewEquipment ?? false
      });
    }
  }, [insured_item, quote]);

  // Há dados da cotação/item segurado para mostrar (evita check em campos que ficaram vazios).
  const hasQuoteData = Boolean(displayValues.typeEquipment) || displayValues.value > 0;

  const formatItemSelect = (items: Array<Manufacturer>) =>
    items?.map((item) => ({
      value: item.id,
      label: item.name
    }));

  const formatItemYears = () => {
    const year = new Date().getFullYear();
    const items = [];
    for (let index = year; index > year - 10; index -= 1) {
      items.push({ value: index.toString(), label: index.toString() });
    }
    return items;
  };

  const onChangeFile = (key: TypeFile, value: IFile) => {
    setFiles((state) => ({ ...state, [key]: value }));
    setFileErrors((state) => ({ ...state, [key]: false }));
  };

  const onSubmit: SubmitHandler<EquipmentForm> = async (data) => {
    if (!contract_id) {
      storeState.addToast({
        title: 'Não foi possível recuperar o contrato.',
        message: '',
        type: 'warning'
      });
      return;
    }

    const validationErrors = await validateContractFiles(uploadFields, files);
    if (Object.keys(validationErrors).length > 0) {
      setFileErrors(validationErrors);
      return;
    }

    setLoading(true);
    const orgParam = searchParams.get('org');

    try {
      const useInsuredItem = insured_item && (insured_item.year_of_manufacture != null || insured_item.is_new != null);

      await contractAsyncPostEquipment(
        contract_id,
        {
          model: data.model,
          manufacturer_id: String(data.manufacturer_id),
          serial_number: data.serial_number.trim(),
          year_of_manufacture: useInsuredItem ? insured_item.year_of_manufacture : quote?.yearOfManufacture,
          is_new: useInsuredItem ? insured_item.is_new ?? false : quote?.isNewEquipment ?? false
        } as ContractPostEquipment,
        orgParam ?? undefined
      );

      const payload: Record<string, IFile | null> = {};
      uploadFields.forEach((field) => {
        const file = files[field.key];
        if (file) payload[field.key] = file;
      });

      const uploadResponse = await contractAsyncPostFiles(
        contract_id,
        payload as unknown as Parameters<typeof contractAsyncPostFiles>[1],
        orgParam ?? undefined
      );

      if (uploadResponse?.data?.is_recurring_moderation) {
        navigate(contractPath('/contract/download', searchParams));
      } else {
        navigate(contractPath('/contract/emergency', searchParams));
      }
    } catch (error: unknown) {
      logErrorWithContext(error, 'contract_journey_step2', {
        contractId: contract_id,
        manufacturerId: data.manufacturer_id
      });
      storeState.addToast(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  if (hasVoucherError) {
    return <VoucherError />;
  }

  return (
    <ContractJourneyShell
      appId={CONTRACT_JOURNEY_APP_IDS.step2}
      onBack={() => navigate(contractPath('/contract/user', searchParams))}
      progressPercent={78}
    >
      <form data-app-id={`${CONTRACT_JOURNEY_APP_IDS.step2}-form`} onSubmit={handleSubmit(onSubmit)}>
        <ContractStepCard
          actionsAppIdPrefix={CONTRACT_JOURNEY_APP_IDS.step2}
          loading={loading}
          onPrimary={() => {}}
          stepLabel="Passo 2 de 3"
          title="Detalhes do equipamento"
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <InputSelect
              appId={contractStepField(2, 'brand')}
              error={Boolean(errors.manufacturer_id)}
              helperText={errors.manufacturer_id?.message}
              items={formatItemSelect(manufacturers)}
              label="Marca"
              name="brand"
              placeholder="Escolha a marca"
              setValue={(value) => setValue('manufacturer_id', value as string)}
            />
            <Controller
              name="model"
              control={control}
              render={({ field }) => (
                <Input
                  appId={contractStepField(2, 'model')}
                  label="Modelo"
                  placeholder="Descreva o modelo"
                  error={Boolean(errors.model)}
                  isDirty={dirtyFields.model}
                  helperText={errors.model?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name="serial_number"
              control={control}
              render={({ field }) => (
                <Input
                  appId={contractStepField(2, 'serial-number')}
                  label="Chassi do equipamento"
                  placeholder="Informe o chassi do equipamento"
                  error={Boolean(errors.serial_number)}
                  isDirty={dirtyFields.serial_number}
                  helperText={errors.serial_number?.message}
                  {...field}
                />
              )}
            />
            {/*
              Dados já informados na cotação: pré-preenchidos, só de leitura (alteram o preço, não se editam aqui)
              e com o check verde das etapas anteriores. `readOnly` em vez de `disabled` para o valor não parecer
              um placeholder esmaecido.
            */}
            <Input
              appId={contractStepField(2, 'equipment-type')}
              isDirty={Boolean(displayValues.typeEquipment)}
              label="Tipo de equipamento"
              name="typeEquipment"
              readOnly
              value={displayValues.typeEquipment}
            />
            <Input
              appId={contractStepField(2, 'value')}
              inputComponent={NumberFormatCustom as never}
              isDirty={displayValues.value > 0}
              label="Valor"
              name="value"
              readOnly
              startAdornment={<InputAdornment position="start">R$</InputAdornment>}
              value={displayValues.value}
            />
            {displayValues.accessoriesValue > 0 ? (
              <Input
                appId={contractStepField(2, 'accessories-value')}
                inputComponent={NumberFormatCustom as never}
                isDirty
                label="Valor dos acessórios"
                name="accessoriesValue"
                readOnly
                startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                value={displayValues.accessoriesValue}
              />
            ) : null}
            {[
              {
                id: 'has-invoice',
                label: 'Eu tenho nota fiscal',
                checked: displayValues.hasInvoiceDisplay
              },
              {
                id: 'is-new',
                label: 'Meu equipamento é novo (máximo 30 dias da data da compra)',
                checked: displayValues.isNewDisplay
              }
            ].map((row) => (
              <Box
                data-app-id={contractStepField(2, row.id)}
                key={row.id}
                sx={{
                  alignItems: 'center',
                  bgcolor: theme.customColors.backgroundMute,
                  borderRadius: 1,
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'space-between',
                  p: 2
                }}
              >
                <Typography variant="body1">{row.label}</Typography>
                <Box sx={{ alignItems: 'center', display: 'flex', flexShrink: 0, gap: 0.5 }}>
                  <Switch
                    checked={row.checked}
                    data-app-id={contractStepField(2, `${row.id}-switch`)}
                    disabled
                    inputProps={{ 'aria-label': row.label }}
                  />
                  {hasQuoteData ? (
                    <CheckCircleSharpIcon
                      aria-label="Informado na cotação"
                      color="success"
                      data-app-id={contractStepField(2, `${row.id}-check`)}
                    />
                  ) : null}
                </Box>
              </Box>
            ))}
            <InputSelect
              appId={contractStepField(2, 'year-of-manufacture')}
              disabled
              items={formatItemYears()}
              label="Ano de fabricação"
              name="yearOfManufacture"
              defaultValue={displayValues.yearOfManufacture}
              setValue={() => {}}
            />

            <ContractUploadSection
              errors={fileErrors as FilesError}
              fields={uploadFields}
              files={files}
              onChangeFile={onChangeFile}
              showVoucherHint={!hasInvoice}
              voucher={voucher}
            />
          </Box>
        </ContractStepCard>
      </form>
    </ContractJourneyShell>
  );
}
